<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const subMenus = [
  {
    title: 'Pengaturan Toko',
    description: 'Konfigurasi nama toko, alamat, pajak, format struk, dan preferensi sistem',
    icon: 'ri-settings-3-line',
    color: 'primary',
    to: '/userguide/sistem/pengaturan',
    tags: ['Toko', 'Pajak', 'Struk', 'Format', 'Preferensi'],
    estimasi: '6 menit',
  },
  {
    title: 'Notifikasi & Export',
    description: 'Sistem notifikasi stok menipis, alert otomatis, dan export laporan Excel/PDF',
    icon: 'ri-notification-3-line',
    color: 'warning',
    to: '/userguide/sistem/notifikasi',
    tags: ['Notifikasi', 'Export', 'PDF', 'Excel', 'Alert', 'Laporan'],
    estimasi: '5 menit',
  },
  {
    title: 'User & Role Management',
    description: 'Tambah karyawan ke sistem, atur hak akses kasir vs manager vs owner',
    icon: 'ri-group-line',
    color: 'info',
    to: '/userguide/sistem/user-role',
    tags: ['Karyawan', 'Kasir', 'Manager', 'Hak Akses', 'Password'],
    estimasi: '8 menit',
  },
  {
    title: 'Audit Log',
    description: 'Pantau semua aktivitas pengguna — siapa mengubah apa, jam berapa, dari nilai berapa menjadi berapa',
    icon: 'ri-shield-check-line',
    color: 'error',
    to: '/userguide/sistem/audit-log',
    tags: ['Keamanan', 'Monitoring', 'Riwayat', 'Investigasi'],
    estimasi: '7 menit',
  },
]

const highlights = [
  {
    icon: 'ri-notification-3-line',
    color: 'warning',
    judul: 'Notifikasi Otomatis',
    deskripsi: 'Sistem kirim peringatan stok menipis setiap jam 08.00 tanpa Anda perlu melakukan apapun',
  },
  {
    icon: 'ri-download-2-line',
    color: 'success',
    judul: 'Export Laporan',
    deskripsi: 'Download laporan penjualan dan laba rugi dalam format Excel atau PDF kapan saja',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'error',
    judul: 'Audit Trail Penuh',
    deskripsi: 'Setiap perubahan data tercatat otomatis — siapa, kapan, dan apa yang diubah',
  },
  {
    icon: 'ri-group-line',
    color: 'primary',
    judul: 'Multi-User Aman',
    deskripsi: 'Kasir hanya bisa akses fitur yang dibutuhkan, owner bisa pantau segalanya',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Sistem</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="grey" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-settings-3-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Panduan Sistem & Administrasi</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Notifikasi otomatis, export laporan, manajemen karyawan, dan audit keamanan toko
            </p>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Highlight fitur -->
    <VRow class="mb-2">
      <VCol v-for="h in highlights" :key="h.judul" cols="12" sm="6" md="3">
        <VCard variant="tonal" :color="h.color" class="h-100">
          <VCardText class="text-center py-4">
            <VIcon :icon="h.icon" size="32" class="mb-2" />
            <p class="text-body-2 font-weight-bold mb-1">{{ h.judul }}</p>
            <p class="text-caption mb-0">{{ h.deskripsi }}</p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Sub Menu Cards (4 KOTAK) -->
    <VRow class="mt-2">
      <VCol v-for="menu in subMenus" :key="menu.title" cols="12" md="3">
        <RouterLink :to="menu.to" class="text-decoration-none">
          <VCard variant="outlined" hover class="h-100 guide-card">
            <VCardText>
              <div class="d-flex align-center gap-3 mb-3">
                <VAvatar :color="menu.color" variant="tonal" size="48" rounded>
                  <VIcon :icon="menu.icon" size="26" />
                </VAvatar>
                <div>
                  <p class="text-body-1 font-weight-bold mb-0">{{ menu.title }}</p>
                  <VChip size="x-small" color="secondary" variant="tonal">
                    ⏱ {{ menu.estimasi }} belajar
                  </VChip>
                </div>
              </div>
              <p class="text-body-2 text-medium-emphasis mb-3">{{ menu.description }}</p>
              <div class="d-flex flex-wrap gap-1">
                <VChip
                  v-for="tag in menu.tags"
                  :key="tag"
                  size="x-small"
                  variant="tonal"
                  :color="menu.color"
                >
                  {{ tag }}
                </VChip>
              </div>
            </VCardText>
            <VCardActions class="pt-0 px-4 pb-4">
              <VBtn variant="text" :color="menu.color" append-icon="ri-arrow-right-line" size="small">
                Baca Panduan
              </VBtn>
            </VCardActions>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Urutan Belajar -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="primary" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-route-line" class="me-2" />
            📍 Urutan yang Disarankan untuk Pemilik Baru
          </VCardTitle>
          <VCardText>
            <VTimeline side="end" density="compact" truncate-line="both">
              <VTimelineItem dot-color="primary" size="small">
                <div class="d-flex align-center gap-2">
                  <span class="text-body-2 font-weight-bold">1. Pengaturan Toko</span>
                  <VChip size="x-small" color="primary" variant="tonal">Pertama kali</VChip>
                </div>
                <p class="text-caption text-medium-emphasis mb-0">
                  Isi nama toko, pajak, dan format struk sebelum mulai transaksi
                </p>
              </VTimelineItem>
              <VTimelineItem dot-color="warning" size="small">
                <span class="text-body-2 font-weight-bold">2. Notifikasi & Export</span>
                <p class="text-caption text-medium-emphasis mb-0">
                  Aktifkan alert stok menipis dan pelajari cara export laporan
                </p>
              </VTimelineItem>
              <VTimelineItem dot-color="info" size="small">
                <span class="text-body-2 font-weight-bold">3. User & Role</span>
                <p class="text-caption text-medium-emphasis mb-0">
                  Tambahkan akun untuk kasir dan manager Anda
                </p>
              </VTimelineItem>
              <VTimelineItem dot-color="error" size="small">
                <span class="text-body-2 font-weight-bold">4. Audit Log</span>
                <p class="text-caption text-medium-emphasis mb-0">
                  Pelajari setelah toko berjalan 1 minggu — untuk monitoring keamanan
                </p>
              </VTimelineItem>
            </VTimeline>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
.guide-card {
  transition: all 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    border-color: rgba(var(--v-theme-primary), 0.5) !important;
  }
}
</style>
